import { useContext, useEffect, useState } from 'react';

import { AppContext } from '@/app.context';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('use-is-issue-downloaded');

/**
 * Whether `issueId` already has a Dexie entry, for the issue reader's
 * download-toggle button. `null` while unresolved (db not open yet, or the
 * lookup hasn't returned) -- the button treats that the same as `false`
 * (not yet known to be downloaded) rather than flashing a wrong state.
 */
export function useIsIssueDownloaded(
	issueId: string | undefined,
): [boolean | null, (value: boolean) => void] {
	const { db } = useContext(AppContext);
	const [isDownloaded, setIsDownloaded] = useState<boolean | null>(null);

	useEffect(() => {
		if (!db || !issueId) {
			setIsDownloaded(null);
			return;
		}

		let cancelled = false;
		db.getIssue(issueId)
			.then((issue) => {
				if (!cancelled) {
					setIsDownloaded(!!issue);
				}
			})
			.catch((error: unknown) => {
				logger.error('Failed to check downloaded-issue state', error);
				if (!cancelled) {
					setIsDownloaded(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [db, issueId]);

	return [isDownloaded, setIsDownloaded];
}
